<script setup lang="ts">
/**
 * Settings that are the whole server's, not one person's: when another Claude
 * may start (server/claudeLimit.ts), the CPU cap on everything Claude runs
 * (server/cpuCap.ts), how many Under runs go at once
 * (server/underRuns.ts), the runner's priming prompt
 * (server/primer.ts), the template Claude's brief for a task is filled from
 * (shared/taskPrompt.ts), and the keys client sites send feedback with
 * (FeedbackKeys.vue: every key listed; a new one is made in its repo's settings). Admin only, at the server as well —
 * reached from the top of the stats view (StatsModal, a tap on the usage strip),
 * which offers the way in only to an admin.
 *
 * A WINDOW (lib/windows.ts, drawn by WindowLayer), not a view: put away to the
 * taskbar and back, or closed, over whatever view is on screen. Its header strip
 * is the window's title bar.
 */
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import type { ClaudePrimer, CpuCap } from "@shared/types.js";
import { api } from "../lib/api.js";
import { useBoard } from "../stores/board.js";
import { toast } from "../lib/toast.js";
import { taskPrompt } from "../lib/taskPrompt.js";
import { loadTaskPromptTemplate, saveTaskPromptTemplate, taskPromptTemplate } from "../lib/taskPromptTemplate.js";
import { DEFAULT_TASK_PROMPT, TASK_PROMPT_MAX, TASK_PROMPT_TAGS } from "@shared/taskPrompt.js";
import { CLAUDE_LIMIT_MAX, CLAUDE_START_MB, CLAUDE_MIN_FREE_MB_MAX, type ClaudeLimit, type MachineReading } from "@shared/tmux.js";
import FeedbackKeys from "./FeedbackKeys.vue";
import WorkersCard from "./WorkersCard.vue";
import PinButton from "./PinButton.vue";
import WinMaxButton from "./WinMaxButton.vue";
import { closeWindow, minimizeWindow } from "../lib/windows.js";

const props = defineProps<{ winKey: string }>();
/** server/primer.ts's DEFAULT_PRIMER_PROMPT, which the page cannot import. */
const DEFAULT_PRIMER_PROMPT = "Reply with OK.";

const board = useBoard();
// Focus starts in the window, so Esc (the layer's: put away) reaches it.
const vFocus = { mounted: (el: HTMLElement) => el.focus() };

// ── parallel Claudes ──────────────────────────────────────────────────────
// When another may start: at most so many at work, only with so much RAM free
// and the CPU under so busy. A start past any is refused, and a queued one waits.
type LimitReply = ClaudeLimit & { machine: MachineReading };
const LIMIT_FIELDS = ["minFreeMb", "maxCpu", "max"] as const;
const limit = ref<ClaudeLimit | undefined>(undefined);
const machine = ref<MachineReading | null>(null);
// v-model on a number input hands back a number, or "" when blank.
const limitDraft = reactive<Record<keyof ClaudeLimit, number | "">>({ max: "", minFreeMb: "", maxCpu: "" });
const draftLimit = computed(
  (): ClaudeLimit => ({
    max: limitDraft.max === "" ? null : limitDraft.max,
    minFreeMb: limitDraft.minFreeMb === "" ? null : limitDraft.minFreeMb,
    maxCpu: limitDraft.maxCpu === "" ? null : limitDraft.maxCpu,
  }),
);
const limitBusy = ref(false);
const limitError = ref("");
const limitChanged = computed(() => !!limit.value && LIMIT_FIELDS.some((k) => draftLimit.value[k] !== limit.value![k]));

function tookLimit(r: LimitReply): void {
  limit.value = { max: r.max, minFreeMb: r.minFreeMb, maxCpu: r.maxCpu };
  machine.value = r.machine;
  for (const k of LIMIT_FIELDS) limitDraft[k] = r[k] ?? "";
}

async function loadLimit(): Promise<void> {
  try {
    tookLimit(await api.get<LimitReply>("/api/claude-limit"));
  } catch (err) {
    limitError.value = err instanceof Error ? err.message : String(err);
  }
}

async function saveLimit(): Promise<void> {
  limitBusy.value = true;
  limitError.value = "";
  try {
    tookLimit(await api.put<LimitReply>("/api/claude-limit", draftLimit.value));
    const l = limit.value!;
    const said = [
      l.minFreeMb !== null && `${l.minFreeMb} MB free`,
      l.maxCpu !== null && `CPU under ${l.maxCpu}%`,
      l.max !== null && `at most ${l.max} at once`,
    ].filter(Boolean);
    toast(said.length ? `A Claude starts with ${said.join(", ")}` : "No limit on parallel Claudes");
  } catch (err) {
    limitError.value = err instanceof Error ? err.message : String(err);
  } finally {
    limitBusy.value = false;
  }
}

// ── CPU cap ───────────────────────────────────────────────────────────────
// The most cores every Claude session, and all it starts, may use. Root applies
// it a moment after it is saved, so the page asks again to show it in force.
const cap = ref<CpuCap | undefined>(undefined);
const capDraft = ref<number | "">("");
const capBusy = ref(false);
const capError = ref("");
const capChanged = computed(() => !!cap.value && (capDraft.value === "" ? null : capDraft.value) !== cap.value.cores);
const coresText = (n: number | null): string => (n === null ? "no cap" : `${n} ${n === 1 ? "core" : "cores"}`);
/** Each unit's cap in force, or one figure when they agree. */
const capInForce = computed((): string => {
  const a = cap.value?.applied ?? [];
  if (!a.length) return "unknown: the units could not be read";
  if (a.every((u) => u.cores === a[0]!.cores)) return coresText(a[0]!.cores);
  return a.map((u) => `${u.unit} ${coresText(u.cores)}`).join(", ");
});
/** In force differs from what is asked: root has not applied it (yet). */
const capPending = computed(() => !!cap.value?.set && cap.value.applied.some((u) => u.cores !== cap.value!.cores));

function tookCap(r: CpuCap): void {
  cap.value = r;
  capDraft.value = r.cores ?? "";
}

async function loadCap(): Promise<void> {
  try {
    tookCap(await api.get<CpuCap>("/api/cpu-cap"));
  } catch (err) {
    capError.value = err instanceof Error ? err.message : String(err);
  }
}

async function saveCap(): Promise<void> {
  capBusy.value = true;
  capError.value = "";
  try {
    tookCap(await api.put<CpuCap>("/api/cpu-cap", { cores: capDraft.value === "" ? null : capDraft.value }));
    // The reply waits for root's path unit to apply it, so it says what is in force.
    toast(cap.value!.cores === null ? "No CPU cap on Claude" : `Claude capped at ${coresText(cap.value!.cores)}`);
  } catch (err) {
    capError.value = err instanceof Error ? err.message : String(err);
  } finally {
    capBusy.value = false;
  }
}

// ── Start checks ───────────────────────────────────────────────────────────
// How often queued Ready/Under/Wastage rows and a pill's queued prompts look for room.
const checkSecs = ref<number | undefined>(undefined);
const checkDraft = ref<number | "">("");
const checkBusy = ref(false);
const checkError = ref("");
const checkChanged = computed(() => checkSecs.value !== undefined && checkDraft.value !== "" && checkDraft.value !== checkSecs.value);

async function loadCheck(): Promise<void> {
  try {
    checkSecs.value = (await api.get<{ secs: number }>("/api/start-check")).secs;
    checkDraft.value = checkSecs.value;
  } catch (err) {
    checkError.value = err instanceof Error ? err.message : String(err);
  }
}

async function saveCheck(): Promise<void> {
  checkBusy.value = true;
  checkError.value = "";
  try {
    checkSecs.value = (await api.put<{ secs: number }>("/api/start-check", { secs: checkDraft.value })).secs;
    checkDraft.value = checkSecs.value;
    toast(`Queued Claudes look for room every ${checkSecs.value}s`);
  } catch (err) {
    checkError.value = err instanceof Error ? err.message : String(err);
  } finally {
    checkBusy.value = false;
  }
}

// ── Under runs ────────────────────────────────────────────────────────────
// How many queued Claudes and prompts set to Under are worked on at once: one,
// and one more for every so many percent the week is under its pace.
const underStep = ref<number | null | undefined>(undefined);
const underDraft = ref<number | "">("");
const underBusy = ref(false);
const underError = ref("");
const underChanged = computed(() => underStep.value !== undefined && (underDraft.value === "" ? null : underDraft.value) !== underStep.value);

function tookUnder(r: { step: number | null }): void {
  underStep.value = r.step;
  underDraft.value = r.step ?? "";
}

async function loadUnder(): Promise<void> {
  try {
    tookUnder(await api.get<{ step: number | null }>("/api/under-step"));
  } catch (err) {
    underError.value = err instanceof Error ? err.message : String(err);
  }
}

async function saveUnder(): Promise<void> {
  underBusy.value = true;
  underError.value = "";
  try {
    tookUnder(await api.put<{ step: number | null }>("/api/under-step", { step: underDraft.value === "" ? null : underDraft.value }));
    toast(underStep.value ? `One more Under run for every ${underStep.value}% under the week's pace` : "Under runs go one at a time");
  } catch (err) {
    underError.value = err instanceof Error ? err.message : String(err);
  } finally {
    underBusy.value = false;
  }
}

// ── priming prompt ────────────────────────────────────────────────────────
// What the runner sends to open the five-hour window ahead of a "need Claude"
// entry (runner/prime.ts). Admin only: it is spent on the claude account's plan.
const primer = ref<ClaudePrimer | null>(null);
const primerDraft = ref("");
const primerBusy = ref(false);
const primerError = ref("");
const primerChanged = computed(() => primer.value !== null && primerDraft.value.trim() !== primer.value.prompt);

async function loadPrimer(): Promise<void> {
  try {
    primer.value = (await api.get<{ primer: ClaudePrimer }>("/api/claude-primer")).primer;
    primerDraft.value = primer.value.prompt;
  } catch (err) {
    primerError.value = err instanceof Error ? err.message : String(err);
  }
}

async function savePrimer(): Promise<void> {
  primerBusy.value = true;
  primerError.value = "";
  try {
    primer.value = (await api.put<{ primer: ClaudePrimer }>("/api/claude-primer", { prompt: primerDraft.value })).primer;
    primerDraft.value = primer.value.prompt;
    toast("Saved the priming prompt");
  } catch (err) {
    primerError.value = err instanceof Error ? err.message : String(err);
  } finally {
    primerBusy.value = false;
  }
}

const tokens = (n: number): string => n.toLocaleString();

// ── task prompt template ──────────────────────────────────────────────────
// What the Claude modal's prompt starts as, and what a requeued task is queued
// with: this text with each <tag> replaced by that part of the task.
const templateDraft = ref("");
const templateLoaded = ref(false);
const templateBusy = ref(false);
const templateError = ref("");
const templateBox = ref<HTMLTextAreaElement | null>(null);
const templateChanged = computed(
  () => templateLoaded.value && (templateDraft.value.trim() || DEFAULT_TASK_PROMPT) !== taskPromptTemplate.value,
);

async function loadTemplate(): Promise<void> {
  await loadTaskPromptTemplate();
  templateDraft.value = taskPromptTemplate.value;
  templateLoaded.value = true;
}

async function saveTemplate(): Promise<void> {
  templateBusy.value = true;
  templateError.value = "";
  try {
    templateDraft.value = await saveTaskPromptTemplate(templateDraft.value);
    toast("Saved the task prompt");
  } catch (err) {
    templateError.value = err instanceof Error ? err.message : String(err);
  } finally {
    templateBusy.value = false;
  }
}

/** Put `<tag>` where the caret is, replacing any selection. */
async function insertTag(tag: string): Promise<void> {
  const box = templateBox.value;
  const text = `<${tag}>`;
  const at = box ? box.selectionStart : templateDraft.value.length;
  const end = box ? box.selectionEnd : at;
  templateDraft.value = templateDraft.value.slice(0, at) + text + templateDraft.value.slice(end);
  await nextTick();
  box?.focus();
  box?.setSelectionRange(at + text.length, at + text.length);
}

/** Wrap the selection in `[…]`, an optional part, leaving the caret inside it. */
async function insertOptional(): Promise<void> {
  const box = templateBox.value;
  const at = box ? box.selectionStart : templateDraft.value.length;
  const end = box ? box.selectionEnd : at;
  templateDraft.value = `${templateDraft.value.slice(0, at)}[${templateDraft.value.slice(at, end)}]${templateDraft.value.slice(end)}`;
  await nextTick();
  box?.focus();
  box?.setSelectionRange(end + 1, end + 1);
}

/** Filled from the open task, else one with a parent, so <parents> and <parent-concat> show something. */
const previewTask = computed(() => {
  if (board.selected) return board.selected;
  let any = null;
  for (const t of board.tasks.values()) {
    if (t.parentId) return t;
    any ??= t;
  }
  return any;
});
const preview = computed(() =>
  previewTask.value ? taskPrompt(previewTask.value, templateDraft.value.trim() || DEFAULT_TASK_PROMPT) : "",
);

onMounted(() => {
  void loadLimit();
  void loadCap();
  void loadUnder();
  void loadCheck();
  void loadPrimer();
  void loadTemplate();
});
</script>

<template>
  <section class="global-settings" aria-label="Global settings">
    <header class="diff-summary global-settings-head">
      <strong class="global-settings-title">Global settings</strong>
      <PinButton class="global-settings-pin" :pin="{ kind: 'settings' }" name="Global settings" />
      <button class="btn ghost" type="button" title="Put away — the taskbar brings it back" aria-label="Minimize" @click="minimizeWindow(props.winKey)">–</button>
      <WinMaxButton :win-key="props.winKey" />
      <button v-focus class="btn ghost" type="button" aria-label="Close" @click="closeWindow(props.winKey)">×</button>
    </header>
    <div class="profile global-settings-body">
    <div class="profile-inner">
      <div class="profile-sub">For everyone on this server. Only admins can see or change them.</div>

      <div class="money-card profile-card">
        <h3>Parallel Claudes</h3>
        <p class="money-hint">
          When another Claude may start. It needs the RAM free and the CPU under the share busy set here —
          a Claude started in the last half minute counts as {{ CLAUDE_START_MB }} MB taken, since the reading
          does not show it yet — and, if a most at once is set, fewer at work than that: working, waiting on a
          question, or just started; an idle one does not count. Otherwise a start is refused, and a queued
          Claude set to Ready, Under or Wastage and a pill's queued prompt wait until there is room. Auto-fix after a failed deploy is
          never held back. Blank is no limit.
        </p>
        <div class="integration-row claude-limit-row">
          <label class="claude-limit-field">
            RAM free
            <input
              v-model="limitDraft.minFreeMb"
              class="claude-limit"
              type="number"
              inputmode="numeric"
              min="1"
              :max="CLAUDE_MIN_FREE_MB_MAX"
              step="128"
              placeholder="Any"
              aria-label="RAM that must be free to start a Claude, in MB"
              :disabled="!limit || limitBusy"
              @keydown.enter="limitChanged && saveLimit()"
            />
            MB
          </label>
          <label class="claude-limit-field">
            CPU under
            <input
              v-model="limitDraft.maxCpu"
              class="claude-limit"
              type="number"
              inputmode="numeric"
              min="1"
              max="100"
              step="5"
              placeholder="Any"
              aria-label="The CPU's busy share over the last minute a Claude may start under, in percent"
              :disabled="!limit || limitBusy"
              @keydown.enter="limitChanged && saveLimit()"
            />
            %
          </label>
          <label class="claude-limit-field">
            At most
            <input
              v-model="limitDraft.max"
              class="claude-limit"
              type="number"
              inputmode="numeric"
              min="1"
              :max="CLAUDE_LIMIT_MAX"
              step="1"
              placeholder="Any"
              aria-label="Most Claudes at work at once"
              :disabled="!limit || limitBusy"
              @keydown.enter="limitChanged && saveLimit()"
            />
            at once
          </label>
          <button class="btn primary" type="button" :disabled="!limitChanged || limitBusy" @click="saveLimit">Save</button>
        </div>
        <p v-if="machine" class="integration-muted claude-limit-now">
          Now: {{ Math.round(machine.freeMb) }} MB free<template v-if="machine.cpu !== null">, CPU {{ Math.round(machine.cpu) }}% busy over the last minute</template>.
        </p>
        <p v-if="limitError" class="integration-error">{{ limitError }}</p>
      </div>

      <div class="money-card profile-card">
        <h3>CPU cap</h3>
        <p class="money-hint">
          The most of the box's {{ cap?.cpus ?? "" }} cores that the Claudes may use: every session and everything it starts —
          builds, tests, a Deploy from here. todos-tmux and todos-runner each get this many, and both always give way to the
          live sites. The box's root applies it a moment after it is saved. Blank is no cap.
        </p>
        <div class="integration-row claude-limit-row">
          <label class="claude-limit-field">
            At most
            <input
              v-model="capDraft"
              class="claude-limit"
              type="number"
              inputmode="numeric"
              min="1"
              :max="cap?.cpus"
              step="1"
              placeholder="All"
              aria-label="Most cores the Claudes may use"
              :disabled="!cap || capBusy"
              @keydown.enter="capChanged && saveCap()"
            />
            cores
          </label>
          <button class="btn primary" type="button" :disabled="!capChanged || capBusy" @click="saveCap">Save</button>
        </div>
        <p v-if="cap" class="integration-muted claude-limit-now">
          In force: {{ capInForce }}.<template v-if="!cap.set"> Never set here: that is what the box was given by hand.</template>
          <template v-else-if="capPending"> Not yet what is set here: the box has not applied it.</template>
        </p>
        <p v-if="capError" class="integration-error">{{ capError }}</p>
      </div>

      <div class="money-card profile-card">
        <h3>Under runs</h3>
        <p class="money-hint">
          How many queued Claudes and prompts set to Under are worked on at once while the week is under its pace:
          one, and one more for every so many percent under — at 5, a week 8% under its pace runs two, and 10% under
          three. A task counts until it leaves doing, a prompt until its work is over. Blank is only ever one at a
          time. In wastage everything goes, whatever is set here.
        </p>
        <div class="integration-row claude-limit-row">
          <label class="claude-limit-field">
            One more for every
            <input
              v-model="underDraft"
              class="claude-limit"
              type="number"
              inputmode="numeric"
              min="1"
              max="100"
              step="1"
              placeholder="Never"
              aria-label="Percent under the week's pace that lets one more Under run go at once"
              :disabled="underStep === undefined || underBusy"
              @keydown.enter="underChanged && saveUnder()"
            />
            % under
          </label>
          <button class="btn primary" type="button" :disabled="!underChanged || underBusy" @click="saveUnder">Save</button>
        </div>
        <p v-if="underError" class="integration-error">{{ underError }}</p>
      </div>

      <div class="money-card profile-card">
        <h3>Start checks</h3>
        <p class="money-hint">
          How often queued Claudes set to Ready, Under or Wastage, and a pill's queued prompts, look again for room and for the
          plan usage. A Claude held back by the limits above goes this long, at most, after there is room.
        </p>
        <div class="integration-row claude-limit-row">
          <label class="claude-limit-field">
            Every
            <input
              v-model="checkDraft"
              class="claude-limit"
              type="number"
              inputmode="numeric"
              min="1"
              max="3600"
              step="1"
              aria-label="Seconds between looks for room to start a queued Claude"
              :disabled="checkSecs === undefined || checkBusy"
              @keydown.enter="checkChanged && saveCheck()"
            />
            seconds
          </label>
          <button class="btn primary" type="button" :disabled="!checkChanged || checkBusy" @click="saveCheck">Save</button>
        </div>
        <p v-if="checkError" class="integration-error">{{ checkError }}</p>
      </div>

      <WorkersCard />

      <div class="money-card profile-card">
        <h3>Priming prompt</h3>
        <p class="money-hint">
          Sent by the runner five hours before a "need Claude" entry, on Haiku, so the plan's window resets
          as the entry starts. The prompt and its answer both come out of that window.
        </p>
        <textarea
          v-model="primerDraft"
          class="primer-prompt"
          rows="3"
          maxlength="4000"
          :disabled="primer === null || primerBusy"
          :placeholder="DEFAULT_PRIMER_PROMPT"
          @keydown.enter.meta.prevent="primerChanged && savePrimer()"
          @keydown.enter.ctrl.prevent="primerChanged && savePrimer()"
        ></textarea>
        <div class="integration-row">
          <button class="btn primary" type="button" :disabled="!primerChanged || primerBusy" @click="savePrimer">Save</button>
          <button
            class="btn ghost"
            type="button"
            title="Put the default text in the box, to edit from"
            :disabled="primer === null || primerBusy || primerDraft === DEFAULT_PRIMER_PROMPT"
            @click="primerDraft = DEFAULT_PRIMER_PROMPT"
          >Set to default</button>
          <span class="integration-muted">Blank puts back “Reply with OK.”</span>
        </div>
        <template v-if="primer?.last">
          <div class="integration-row primer-last-head">
            <span>Last response</span>
            <span class="integration-muted">
              {{ new Date(primer.last.at).toLocaleString() }}
              <template v-if="primer.last.inputTokens !== null && primer.last.outputTokens !== null">
                · {{ tokens(primer.last.inputTokens + primer.last.outputTokens) }} tokens
                ({{ tokens(primer.last.inputTokens) }} in, {{ tokens(primer.last.outputTokens) }} out)
              </template>
            </span>
          </div>
          <p v-if="primer.last.prompt && primer.last.prompt !== primer.prompt" class="integration-muted primer-was">
            Sent with an earlier prompt: “{{ primer.last.prompt }}”
          </p>
          <p v-if="primer.last.error" class="integration-error">{{ primer.last.error }}</p>
          <pre v-else class="integration-code primer-response">{{ primer.last.response ?? "(no answer)" }}</pre>
        </template>
        <p v-else-if="primer" class="integration-muted primer-was">Nothing primed yet.</p>
        <p v-if="primerError" class="integration-error">{{ primerError }}</p>
      </div>

      <div class="money-card profile-card">
        <h3>Task prompt</h3>
        <p class="money-hint">
          What Claude is briefed with when it is started or queued on a task. Each tag is replaced with that
          part of the task; a line holding only an empty one is left out. Square brackets
          round a tag take its label with it: <code>[Notes: &lt;notes&gt;]</code> is left out whole while the tag is empty.
        </p>
        <div class="integration-row task-prompt-tags">
          <button
            v-for="t in TASK_PROMPT_TAGS"
            :key="t.tag"
            type="button"
            class="btn ghost task-prompt-tag"
            :title="`${t.what} — click to insert`"
            :disabled="!templateLoaded || templateBusy"
            @click="insertTag(t.tag)"
          >&lt;{{ t.tag }}&gt;</button>
          <button
            type="button"
            class="btn ghost task-prompt-tag"
            title="Brackets round a tag: left out, label and all, while every tag inside is empty — click to wrap the selection"
            :disabled="!templateLoaded || templateBusy"
            @click="insertOptional"
          >[…]</button>
        </div>
        <textarea
          ref="templateBox"
          v-model="templateDraft"
          class="primer-prompt task-prompt-template"
          rows="10"
          :maxlength="TASK_PROMPT_MAX"
          :disabled="!templateLoaded || templateBusy"
          :placeholder="DEFAULT_TASK_PROMPT"
          @keydown.enter.meta.prevent="templateChanged && saveTemplate()"
          @keydown.enter.ctrl.prevent="templateChanged && saveTemplate()"
        ></textarea>
        <div class="integration-row">
          <button class="btn primary" type="button" :disabled="!templateChanged || templateBusy" @click="saveTemplate">Save</button>
          <button
            class="btn ghost"
            type="button"
            title="Put the default text in the box, to edit from"
            :disabled="!templateLoaded || templateBusy || templateDraft === DEFAULT_TASK_PROMPT"
            @click="templateDraft = DEFAULT_TASK_PROMPT"
          >Set to default</button>
          <span class="integration-muted">Blank puts back the default.</span>
        </div>
        <template v-if="previewTask">
          <div class="integration-row primer-last-head">
            <span>Preview</span>
            <span class="integration-muted">{{ previewTask.title }}</span>
          </div>
          <pre class="integration-code primer-response">{{ preview }}</pre>
        </template>
        <p v-if="templateError" class="integration-error">{{ templateError }}</p>
      </div>

      <FeedbackKeys />
    </div>
    </div>
  </section>
</template>
